/**
 * Phase 5.3 (2026-05-15) — Notifications Settings (admin/staff scope).
 *
 * Single page at `/notifications` that lets a staff user:
 *   • see whether push is enabled on this browser
 *   • subscribe / unsubscribe at the browser level
 *   • toggle the 5 categories individually (server-side prefs)
 *   • fire a "Test reçu ✅" push to themselves
 *
 * iOS < 16.4 (no PushManager) → UI is read-only with an explanatory hint.
 * The page is gated by ProtectedRoute (admin / user / technicien).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Bell, BellOff, BellRing, CheckCircle2, AlertTriangle, Loader2, Wrench,
  Receipt, FileText, MessageCircle, CheckCheck, Send, Smartphone,
} from 'lucide-react';

// Backend category ids (never translated) + i18n subkeys for the visible label/description.
const CATEGORIES = [
  { id: 'new_ticket',            icon: Wrench,         labelKey: 'notifSettings.catNewTicketLabel',   descKey: 'notifSettings.catNewTicketDesc' },
  { id: 'ticket_ready',          icon: CheckCheck,     labelKey: 'notifSettings.catTicketReadyLabel', descKey: 'notifSettings.catTicketReadyDesc' },
  { id: 'new_invoice',           icon: Receipt,        labelKey: 'notifSettings.catNewInvoiceLabel',  descKey: 'notifSettings.catNewInvoiceDesc' },
  { id: 'new_chat_message',      icon: MessageCircle,  labelKey: 'notifSettings.catNewChatLabel',     descKey: 'notifSettings.catNewChatDesc' },
  { id: 'new_or_accepted_devis', icon: FileText,       labelKey: 'notifSettings.catDevisLabel',       descKey: 'notifSettings.catDevisDesc' },
];

const pushSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

// VAPID public key → Uint8Array (web-push spec).
const urlBase64ToUint8Array = (base64) => {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
};

const NotificationsSettingsPage = () => {
  const { api, user } = useAuth();
  const { t } = useLanguage();
  const [supported] = useState(pushSupported());
  const [permission, setPermission] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'default');
  const [browserSub, setBrowserSub] = useState(null);
  const [serverStatus, setServerStatus] = useState({ active_count: 0, total_count: 0, categories: {} });
  const [busy, setBusy] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get('/push/status');
      setServerStatus(data);
    } catch (err) {
      console.error('push status failed', err);
    }
    if (supported) {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setBrowserSub(sub);
        if (typeof Notification !== 'undefined') setPermission(Notification.permission);
      } catch (err) {
        console.warn('SW subscription read failed', err);
      }
    }
  }, [api, supported]);

  useEffect(() => { refresh(); }, [refresh]);

  const isEnabled = supported && permission === 'granted' && !!browserSub && serverStatus.active_count > 0;
  const isBlocked = supported && permission === 'denied';

  const handleEnable = async () => {
    setError(''); setInfo(''); setBusy(true);
    try {
      const reg = await navigator.serviceWorker.register('/sw.js');
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        setError(perm === 'denied'
          ? t('notifSettings.errPermDenied')
          : t('notifSettings.errPermNotGranted'));
        return;
      }
      const { data: vapid } = await api.get('/push/public-key');
      if (!vapid?.public_key) {
        setError(t('notifSettings.errVapidUnavailable'));
        return;
      }
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapid.public_key),
        });
      }
      await api.post('/push/subscribe', { subscription: sub.toJSON() });
      setBrowserSub(sub);
      setInfo(t('notifSettings.infoEnabled'));
      await refresh();
    } catch (err) {
      console.error('enable push failed', err);
      setError(err.message || t('notifSettings.errEnableFailed'));
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    setError(''); setInfo(''); setBusy(true);
    try {
      let endpoint = browserSub?.endpoint;
      if (browserSub) {
        try { await browserSub.unsubscribe(); } catch (_) { /* noop */ }
      }
      await api.delete('/push/subscribe' + (endpoint ? `?endpoint=${encodeURIComponent(endpoint)}` : ''));
      setBrowserSub(null);
      setInfo(t('notifSettings.infoDisabled'));
      await refresh();
    } catch (err) {
      console.error('disable push failed', err);
      setError(err.message || t('notifSettings.errDisableFailed'));
    } finally {
      setBusy(false);
    }
  };

  const handleToggleCategory = async (cat, next) => {
    // Optimistic update.
    setServerStatus((prev) => ({ ...prev, categories: { ...prev.categories, [cat]: next } }));
    try {
      await api.patch('/push/preferences', { categories: { [cat]: next } });
    } catch (err) {
      console.error('toggle pref failed', err);
      setError(t('notifSettings.errPrefsUpdate'));
      await refresh();
    }
  };

  const handleTest = async () => {
    setError(''); setInfo(''); setTestBusy(true);
    try {
      const { data } = await api.post('/push/test');
      if (data?.sent > 0) {
        setInfo(t('notifSettings.infoTestSent', { sent: data.sent, subs: data.subs }));
      } else {
        setError(t('notifSettings.errNoneSent'));
      }
    } catch (err) {
      console.error('test push failed', err);
      setError(err.response?.data?.detail || t('notifSettings.errTestFailed'));
    } finally {
      setTestBusy(false);
    }
  };

  const statusBadge = useMemo(() => {
    if (!supported) return { label: t('notifSettings.statusUnsupported'), cls: 'text-gray-300 bg-gray-500/15 border-gray-500/30', icon: Smartphone };
    if (isBlocked) return { label: t('notifSettings.statusBlocked'), cls: 'text-red-300 bg-red-500/15 border-red-500/30', icon: BellOff };
    if (isEnabled) return { label: t('notifSettings.statusEnabled'), cls: 'text-emerald-300 bg-emerald-500/15 border-emerald-500/30', icon: BellRing };
    return { label: t('notifSettings.statusDisabled'), cls: 'text-amber-300 bg-amber-500/15 border-amber-500/30', icon: Bell };
  }, [supported, isBlocked, isEnabled, t]);
  const StatusIcon = statusBadge.icon;

  return (
    <Layout>
      <div className="space-y-6 p-4 md:p-6 max-w-3xl mx-auto" data-testid="notifications-settings-page">
        <header>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">{t('notifSettings.title')}</h1>
          <p className="text-sm text-gray-400 mt-1">
            {t('notifSettings.intro')}
          </p>
        </header>

        {/* Status card */}
        <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border ${statusBadge.cls}`}
                    data-testid="push-status-badge">
                <StatusIcon className="w-4 h-4" />
                {statusBadge.label}
              </span>
              <span className="text-xs text-gray-500" data-testid="push-active-count">
                {t('notifSettings.activeCount', { count: serverStatus.active_count })}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {supported && !isBlocked && !isEnabled && (
                <button
                  onClick={handleEnable}
                  disabled={busy}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/30 disabled:opacity-50 text-sm font-medium"
                  data-testid="push-enable-btn"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
                  {t('notifSettings.enableBtn')}
                </button>
              )}
              {isEnabled && (
                <>
                  <button
                    onClick={handleTest}
                    disabled={testBusy}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-sky-500/20 border border-sky-500/40 text-sky-200 hover:bg-sky-500/30 disabled:opacity-50 text-sm font-medium"
                    data-testid="push-test-btn"
                  >
                    {testBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {t('notifSettings.testBtn')}
                  </button>
                  <button
                    onClick={handleDisable}
                    disabled={busy}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 text-gray-200 disabled:opacity-50 text-sm font-medium"
                    data-testid="push-disable-btn"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <BellOff className="w-4 h-4" />}
                    {t('notifSettings.disableBtn')}
                  </button>
                </>
              )}
            </div>
          </div>
          {!supported && (
            <p className="text-xs text-gray-400 mt-3" data-testid="push-ios-hint">
              <Smartphone className="w-3.5 h-3.5 inline-block mr-1" />
              {t('notifSettings.iosHintPre')}{' '}<strong>iOS 16.4+</strong>
              {' '}{t('notifSettings.iosHintPost')}
            </p>
          )}
          {isBlocked && (
            <p className="text-xs text-red-300 mt-3" data-testid="push-blocked-hint">
              <AlertTriangle className="w-3.5 h-3.5 inline-block mr-1" />
              {t('notifSettings.blockedHint')}
            </p>
          )}
          {info && (
            <p className="text-xs text-emerald-300 mt-3" data-testid="push-info">
              <CheckCircle2 className="w-3.5 h-3.5 inline-block mr-1" /> {info}
            </p>
          )}
          {error && (
            <p className="text-xs text-red-300 mt-3" data-testid="push-error">
              <AlertTriangle className="w-3.5 h-3.5 inline-block mr-1" /> {error}
            </p>
          )}
        </div>

        {/* Category toggles */}
        <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
          <h2 className="text-base font-semibold mb-1">{t('notifSettings.categoriesTitle')}</h2>
          <p className="text-xs text-gray-500 mb-4">
            {t('notifSettings.categoriesHint')}
          </p>
          <div className="space-y-2">
            {CATEGORIES.map(({ id, labelKey, icon: Icon, descKey }) => {
              const checked = !!serverStatus.categories?.[id];
              const disabled = !isEnabled;
              return (
                <label
                  key={id}
                  className={`flex items-start gap-3 p-3 rounded-xl border transition-all ${
                    disabled
                      ? 'border-white/5 bg-white/[0.02] opacity-50 cursor-not-allowed'
                      : 'border-white/10 bg-white/[0.04] hover:bg-white/[0.06] cursor-pointer'
                  }`}
                  data-testid={`push-cat-row-${id}`}
                >
                  <div className="mt-0.5 p-1.5 rounded-lg bg-white/5 border border-white/10">
                    <Icon className="w-4 h-4 text-gray-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-100">{t(labelKey)}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{t(descKey)}</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={disabled}
                    onChange={(e) => handleToggleCategory(id, e.target.checked)}
                    className="mt-1 h-5 w-9 appearance-none rounded-full bg-white/10 border border-white/15 transition-all cursor-pointer relative
                               checked:bg-emerald-500/50 checked:border-emerald-400/60
                               before:content-[''] before:absolute before:top-0.5 before:left-0.5 before:w-4 before:h-4 before:rounded-full before:bg-white before:transition-all
                               checked:before:left-[18px]"
                    data-testid={`push-cat-toggle-${id}`}
                  />
                </label>
              );
            })}
          </div>
        </div>

        {/* Diagnostic footer */}
        <p className="text-[11px] text-gray-600 text-center" data-testid="push-user-id">
          {t('notifSettings.accountLabel')}: <code>{user?.username}</code> • {t('notifSettings.roleLabel')}: <code>{user?.role}</code>
          {' '}• {t('notifSettings.totalSubsLabel')}: <code>{serverStatus.total_count}</code>
        </p>
      </div>
    </Layout>
  );
};

export default NotificationsSettingsPage;
